import { type Transaction } from "prosemirror-state";
import { type EditorView } from "prosemirror-view";

import * as prosemirrorHelpers from "~/components/canvas/easel/prosemirror/helpers";
import { cellToNodes } from "~/components/canvas/easel/prosemirror/serialization/document";
import { type Cell } from "~/models/cell";
import { type useSendToCodeEditorStore } from "~/state/machines/code-editor";

import { TEXT_PLACEHOLDER_PLUGIN_KEY } from "../easel/prosemirror/plugins/text-placeholder-plugin/textPlaceholderPlugin";

type FocusPositionArgs = {
	view: EditorView;
	position: number;
};

export const focusPosition = ({ view, position }: FocusPositionArgs) => {
	const tr = prosemirrorHelpers.focusPosition(view.state, position);
	view.dispatch(tr);
	view.focus();
};

type DeleteTextCellArgs = {
	view: EditorView;
	cellID: string;
};

export const deleteCell = ({ view, cellID }: DeleteTextCellArgs) => {
	const tr = prosemirrorHelpers.deleteCellByID(view.state, cellID);

	if (tr === undefined) {
		return;
	}

	view.dispatch(tr);
};

type ReplaceCellArgs = {
	view: EditorView;
	targetCellID: string;
	cell: Cell;
};
export const replaceCell = ({ view, targetCellID, cell }: ReplaceCellArgs) => {
	const ancestorCell = prosemirrorHelpers.getCellPosition(view.state, targetCellID);
	if (ancestorCell === undefined) {
		return;
	}

	const tr = prosemirrorHelpers.replaceCell({
		tr: view.state.tr,
		ancestorCell,
		node: cellToNodes(cell),
	});

	view.dispatch(tr);
};

export const focusEditor = ({
	view,
	send,
	activePageID,
}: {
	view: EditorView;
	send: ReturnType<typeof useSendToCodeEditorStore>;
	activePageID?: string;
}) => {
	const $from = view.state.selection.$from;

	const ancestorCell = prosemirrorHelpers.findAncestorCell($from);

	if (ancestorCell === undefined) {
		return;
	}

	if (ancestorCell.node.type.name !== "compute_cell") {
		return;
	}

	const cellID = ancestorCell.node.attrs["id"];

	send({ type: "openTab", cellID, activePageID });
};

export const setTextPlaceholderStateBlurred = (tr: Transaction) => {
	tr.setMeta(TEXT_PLACEHOLDER_PLUGIN_KEY, { isFocused: false });
	return tr;
};

export const setTextPlaceholderStateFocused = (tr: Transaction) => {
	tr.setMeta(TEXT_PLACEHOLDER_PLUGIN_KEY, { isFocused: true });
	return tr;
};
